import { useQueries, useQuery } from '@tanstack/react-query';
import { movimentacaoService } from '../../services/movimentacaoService';
import type { Movimentacao } from '../../models';

export const SI_MOVIMENTACOES_QUERY_KEYS = {
  all: ['si', 'movimentacoes'] as const,
  porSolicitacao: (solicitacaoId: string) =>
    [...SI_MOVIMENTACOES_QUERY_KEYS.all, solicitacaoId] as const,
};

const semExcluidas = (movimentacoes: Movimentacao[]) => movimentacoes.filter((m) => !m.excluida);
const soExcluidas = (movimentacoes: Movimentacao[]) => movimentacoes.filter((m) => m.excluida);

/** Só as ativas — excluídas (Movimentacao.excluida) somem de todas as telas por aqui. */
export function useMovimentacoesQuery(solicitacaoId: string | undefined) {
  return useQuery({
    queryKey: SI_MOVIMENTACOES_QUERY_KEYS.porSolicitacao(solicitacaoId ?? ''),
    queryFn: () => movimentacaoService.getBySolicitacao(solicitacaoId as string),
    enabled: Boolean(solicitacaoId),
    select: semExcluidas,
  });
}

/** Mesmo cache de useMovimentacoesQuery, só as excluídas — pra seção "Excluídas" do detalhe. */
export function useMovimentacoesExcluidasQuery(solicitacaoId: string | undefined) {
  return useQuery({
    queryKey: SI_MOVIMENTACOES_QUERY_KEYS.porSolicitacao(solicitacaoId ?? ''),
    queryFn: () => movimentacaoService.getBySolicitacao(solicitacaoId as string),
    enabled: Boolean(solicitacaoId),
    select: soExcluidas,
  });
}

/** Mesmas queries (mesma queryKey) que `useMovimentacoesQuery` busca por linha — usado pra
 * ordenar a lista antes de renderizar, sem duplicar requisições (o cache é compartilhado). */
export function useMovimentacoesPorSolicitacoesQuery(solicitacaoIds: string[]) {
  const resultados = useQueries({
    queries: solicitacaoIds.map((id) => ({
      queryKey: SI_MOVIMENTACOES_QUERY_KEYS.porSolicitacao(id),
      queryFn: () => movimentacaoService.getBySolicitacao(id),
      select: semExcluidas,
    })),
  });

  const porSolicitacao = new Map<string, Movimentacao[]>();
  solicitacaoIds.forEach((id, index) => {
    porSolicitacao.set(id, resultados[index]?.data ?? []);
  });
  return porSolicitacao;
}
